import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { PAGE_PATHS } from '@/services/paths'
import {
  BannerSection,
  VideoWrapper,
  StyledVideo,
  Overlay,
  Content,
  Title,
  Subtitle,
  BannerButton,
  Note
} from './HeroBanner.styles'

const PUBLIC_CDN_BASE = `${process.env.CDN_URL}/public`

const DESKTOP_VIDEO_BY_LOCALE: Record<string, string> = {
  en: `${PUBLIC_CDN_BASE}/Video_Banner_EN.webm`,
  es: `${PUBLIC_CDN_BASE}/Video_Banner_ES.webm`,
  pt: `${PUBLIC_CDN_BASE}/Video_Banner_PT-BR.webm`
}

const MOBILE_VIDEO_SRC = `${PUBLIC_CDN_BASE}/Video_Banner_Vertical.webm`

const HeroBanner: React.FC = () => {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const bannerRef = useRef<HTMLElement | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  const videoSrc = useMemo(() => {
    if (isMobile) return MOBILE_VIDEO_SRC

    const normalizedLanguage = i18n.language?.toLowerCase() || 'pt'

    if (normalizedLanguage.startsWith('en')) {
      return DESKTOP_VIDEO_BY_LOCALE.en
    }

    if (normalizedLanguage.startsWith('es')) {
      return DESKTOP_VIDEO_BY_LOCALE.es
    }

    return DESKTOP_VIDEO_BY_LOCALE.pt
  }, [i18n.language, isMobile])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(max-width: 900px)')
    const updateIsMobile = (event?: MediaQueryListEvent) => {
      setIsMobile(event ? event.matches : mediaQuery.matches)
    }

    updateIsMobile()

    mediaQuery.addEventListener('change', updateIsMobile)

    return () => {
      mediaQuery.removeEventListener('change', updateIsMobile)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleVisibility = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          video.play()
        } else {
          video.pause()
        }
      })
    }

    const observer = new window.IntersectionObserver(handleVisibility, {
      threshold: 0.3
    })
    if (bannerRef.current) {
      observer.observe(bannerRef.current)
    }
    return () => {
      observer.disconnect()
    }
  }, [videoSrc])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    video.load()
    video.play().catch(() => {
      // Ignore autoplay blocking; user interaction can start playback.
    })
  }, [videoSrc])

  return (
    <BannerSection ref={bannerRef}>
      <VideoWrapper>
        <StyledVideo
          ref={videoRef}
          src={videoSrc}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          tabIndex={-1}
          aria-hidden="true"
        />
        <Overlay />
        <Content>
          <Title>{t('landingV2.heroBanner.title')}</Title>
          <Subtitle>{t('landingV2.heroBanner.subtitle')}</Subtitle>
          <BannerButton
            type="button"
            onClick={() => router.push(PAGE_PATHS.contact)}
          >
            {t('landingV2.heroBanner.button')}
          </BannerButton>
          <Note>{t('landingV2.heroBanner.note')}</Note>
        </Content>
      </VideoWrapper>
    </BannerSection>
  )
}

export default HeroBanner
